import React, { useState } from "react";
import { Button } from "@semo/shared-ui";
import { ShieldAlert, CheckCircle2, XCircle, Clock } from "@semo/icons";

import { CONTRACT_RESTRICTION_QUESTIONS } from "../modals/ContractRestrictionAllNoModal";
import { ContractRestrictionBuyerConfirmModal } from "../modals/ContractRestrictionBuyerConfirmModal";
import { useChatActions } from "../../context/ChatActionContext";

/**
 * CONTRACT_RESTRICTION_CONFIRM 메시지 (수의계약 체결 여부 확인 요청 카드)
 *
 * 공급사가 paperCode 135 문항에 "예"로 응답해 제출하면 채팅방에 남는 카드.
 * 종전에는 푸시 알림 + [계약 서류 관리] 카드 배너뿐이라 발주기관이 확인 요청을 놓쳤다.
 *
 * 상태별 렌더:
 * - flaggedIndexes 비어있음: 공급사가 재제출로 "예"→"아니오" 정정 → 해소 안내만
 * - buyerConfirmation=null: 발주기관에게 [확인하기] CTA / 공급사에게는 대기 안내
 * - buyerConfirmation 확정: 최종 응답 결과 표시 (1회 확정이라 재진입 없음)
 */

export interface ContractRestrictionConfirmData {
  contractId: string;
  documentRequestMessageId: string;
  answers: string[];
  flaggedIndexes: number[];
  submittedAt: string;
  buyerConfirmation: "YES" | "NO" | null;
  confirmedAt: string | null;
}

interface ContractRestrictionConfirmMessageProps {
  message: {
    roomId: string;
    contractRestrictionConfirmData?: ContractRestrictionConfirmData;
  };
  role?: "buyer" | "supplier";
}

export const ContractRestrictionConfirmMessage: React.FC<
  ContractRestrictionConfirmMessageProps
> = ({ message, role }) => {
  const { onSubmitContractDocumentBuyerConfirmation } = useChatActions();
  const [modalOpen, setModalOpen] = useState(false);

  const data = message.contractRestrictionConfirmData;
  const isSupplier = role === "supplier";

  if (!data) return null;

  const flaggedIndexes = data.flaggedIndexes ?? [];
  const flaggedQuestions = flaggedIndexes.map(
    (index) =>
      CONTRACT_RESTRICTION_QUESTIONS[index] ?? `${String(index + 1)}번 문항`,
  );
  const buyerConfirmation = data.buyerConfirmation ?? null;
  const needsBuyerConfirmation =
    flaggedIndexes.length > 0 && buyerConfirmation === null;
  const canConfirm =
    !isSupplier &&
    needsBuyerConfirmation &&
    Boolean(onSubmitContractDocumentBuyerConfirmation);

  return (
    <div className="flex justify-center w-full px-1 sm:px-0 mb-4">
      <div className="w-full rounded-xl border border-amber-300 bg-amber-50 overflow-hidden shadow-sm">
        <div className="px-4 py-3 flex items-center gap-2 border-b border-amber-200/70">
          <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
          <span className="text-sm font-semibold text-amber-900">
            수의계약 체결 여부 확인 요청
          </span>
        </div>

        <div className="px-4 py-3">
          {flaggedIndexes.length === 0 ? (
            <p className="text-xs leading-relaxed text-amber-800">
              공급사가 응답을 정정해 전 문항이 &quot;아니오&quot;가 되었습니다.
              추가 확인이 필요하지 않습니다.
            </p>
          ) : (
            <>
              <p className="text-xs leading-relaxed text-amber-800">
                {isSupplier
                  ? '아래 항목에 "예"로 응답해 발주기관 최종 확인을 기다리는 중입니다.'
                  : '공급사가 아래 항목에 "예"로 응답했습니다. 내용을 확인하고 계약 진행 여부를 응답해 주세요.'}
              </p>
              <ul className="mt-2 space-y-1">
                {flaggedQuestions.map((question, idx) => (
                  <li
                    key={flaggedIndexes[idx]}
                    className="text-xs leading-relaxed text-amber-900 pl-3 relative before:content-['·'] before:absolute before:left-0.5"
                  >
                    {question}
                  </li>
                ))}
              </ul>

              {buyerConfirmation === null ? (
                canConfirm ? (
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setModalOpen(true)}
                    >
                      확인하기
                    </Button>
                  </div>
                ) : (
                  isSupplier && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                      <Clock className="w-3.5 h-3.5" />
                      발주기관 확인 대기 중
                    </div>
                  )
                )
              ) : (
                <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold">
                  {buyerConfirmation === "YES" ? (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-emerald-700">
                        발주기관 최종 확인: 예 — 계약 진행
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3.5 h-3.5 text-red-500" />
                      <span className="text-red-600">
                        발주기관 최종 확인: 아니오 — 계약 미진행
                      </span>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {canConfirm && onSubmitContractDocumentBuyerConfirmation && (
        <ContractRestrictionBuyerConfirmModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          answers={data.answers ?? []}
          flaggedIndexes={flaggedIndexes}
          onSubmit={async (buyerConfirmation) => {
            await onSubmitContractDocumentBuyerConfirmation(
              message.roomId,
              buyerConfirmation,
            );
          }}
        />
      )}
    </div>
  );
};
